<template>
  <div
    v-if="question"
    v-question-builder-close="closeQuestionBuilder"
    :class="[event.type, 'QuestionConfigurationBuilder']"
    :style="`top: ${positionTop}px`"
  >
    <span class="Icon arrow-left" />

    <h2
      v-if="!textElementFieldType"
      class="QuestionUuid-header"
    >
      {{ question.question_uuid }}
    </h2>

    <!-- NON QUESTION ELEMENTS -->
    <template v-if="question.non_question">
      <header-image-uploader
        v-if="question.key === 'header_image'"
        :question="question"
      />

      <rmp-input
        v-else-if="question.key === 'form_submit_text'"
        v-model="submitButtonText"
        label="Text"
        :value="event.form_submit_text"
      />
    </template>

    <template v-else-if="question.custom_property_to_update">
      <rmp-input
        v-if="question.custom_property_to_update === 'go_to_next_question_button_label'"
        v-model="question.go_to_next_question_button_label"
        label="Text"
        :value="question.go_to_next_question_button_label"
      />
    </template>

    <template v-else>
      <div v-if="textElementFieldType">
        <p>
          Text
          <tooltip v-if="isDynamicForm">
            <span
              slot="trigger"
              class="u-flex"
            >
              <span class="Icon Icon-info-circle" />
            </span>
            <span slot="tip">
              Smart tags syntax: ${Q2}
            </span>
          </tooltip>
        </p>
        <vue-editor
          v-model="question.label"
          class="VueEditor Label"
          :editor-toolbar="editorToolbar"
        />
      </div>
      <div v-else>
        <p>
          Label

          <tooltip v-if="isDynamicForm">
            <span
              slot="trigger"
              class="u-flex"
            >
              <span class="Icon Icon-info-circle" />
            </span>
            <span slot="tip">
              Smart tags syntax: ${Q2}
            </span>
          </tooltip>

          <vue-editor
            v-model="question.label"
            class="VueEditor Label"
            :editor-toolbar="editorToolbar"
          />
        </p>
      </div>

      <rmp-checkbox
        v-if="!textElementFieldType && !EMAIL_FIELD"
        v-model="question.is_required"
        :value="true"
        label="Required"
      />

      <rmp-text-area
        v-if="fieldsWithCustomOptions"
        v-model="customQuestion.options"
        :class="['TextArea', { 'UniversityQuestion': isUniversityQuestion}]"
        label="Add list options (one per line)"
        @input="handleAddToOptionsList"
      />

      <small
        v-if="isUniversityQuestion && event.can_add_universities"
        style="text-align: left"
      >
        <span class="Icon Icon-info-circle" /> New universities will be associated with UK and created automatically, when the form is saved.
      </small>

      <div class="accordion">
        <fieldset
          v-if="!textElementFieldType && FILE_UPLOAD_FIELD"
          class="Fieldset-collapsible active"
        >
          <legend
            @click="toggleAccordionCollapse"
          >
            Allowed File Types
          </legend>
          <div
            v-if="FILE_UPLOAD_FIELD"
            class="Fieldset-body list-group collapsed FileUploadAllowedFileTypeCheckboxes"
          >
            <rmp-checkbox
              v-model="question.options.allows_documents"
              label="Documents"
              :value="true"
            />
            <div class="AllowedFileTypeExamples">
              <small>E.g: .doc - Microsoft Word</small>
            </div>

            <rmp-checkbox
              v-model="question.options.allows_images"
              label="Images"
              :value="true"
            />

            <div class="AllowedFileTypeExamples">
              <small>E.g: .jpeg .jpg - JPEG Images</small>
            </div>

            <rmp-checkbox
              v-model="question.options.allows_zips"
              label="Zip (Compressed files)"
              :value="true"
            />
            <div class="AllowedFileTypeExamples">
              <small>E.g: .zip - ZIP archive</small>
            </div>

            <p>
              If you want to know exactly what type of files the student can upload please click <span
                class="FileUploadAllowedFilesHelp"
                @click="ui.showFileUploadAllowedFilesModal = true"
              >
                here</span>.
            </p>
            <file-upload-allowed-files-details v-model="ui.showFileUploadAllowedFilesModal" />
          </div>
        </fieldset>

        <fieldset
          v-if="!textElementFieldType && FILE_UPLOAD_FIELD"
          class="Fieldset-collapsible active"
        >
          <legend
            @click="toggleAccordionCollapse"
          >
            Maximum File Size
          </legend>
          <div
            v-if="FILE_UPLOAD_FIELD"
            class="Fieldset-body list-group collapsed"
          >
            <p>This is the maximum file size a student can upload.</p>
            <p>The default limit is 10MB, and the maximum value is 50MB.</p>
            <rmp-input
              v-model="question.options.max_file_size_in_mb"
              label="Max file size (MB)"
              input-type="number"
              :min="1"
              :max="50"
            />
          </div>
        </fieldset>

        <fieldset
          v-if="!textElementFieldType"
          class="Fieldset-collapsible active"
        >
          <legend @click="toggleAccordionCollapse">
            More Options
          </legend>
          <div class="Fieldset-body list-group collapsed">
            <div v-if="question.alternative_types.length > 0">
              <rmp-select
                v-model="question.type"
                :items="question.alternative_types"
                class="QuestionDisplayedAs"
                label="Display as"
                option-value-key="value"
                option-label-key="name"
                :value="question.type"
                :disabled="event.total_records > 0"
              />
              <div
                class="QuestionDisplayedAs-Info"
              >
                <small v-if="event.total_records > 0">
                  <span class="Icon Icon-exclamation-circle" /> You can't change the type when there are records.
                </small>
                <small v-else>By changing the original question type, the <i>"More options"</i> will change accordingly.</small>
              </div>
            </div>
            <rmp-input
              v-if="fieldsWithDefaultValues"
              v-model="question.value"
              label="Default Value"
            />
            <rmp-input
              v-if="question.supports_placeholder"
              v-model="question.placeholder"
              label="Placeholder"
            />
            <rmp-datepicker
              v-if="DATE_FIELD"
              v-model="question.value"
              :clearable="true"
              label="Default Date"
              :disable-dates="{from: null}"
            />

            <template v-if="TIME_FIELD">
              <label class="Form-label">Default Time</label>
              <div
                class="Form-group Time"
              >
                <rmp-select
                  v-model="timeDefaultValue.hour"
                  :items="getNumberArray(1,23,1)"
                  style="width: 100px; margin-right: 10px;"
                  label="Hour"
                  @change="updateTimeDefaultValue()"
                />
                <rmp-select
                  v-model="timeDefaultValue.minute"
                  :items="getNumberArray(0,55,5)"
                  style="width: 100px"
                  label="Minutes"
                  @change="updateTimeDefaultValue()"
                />
              </div>
            </template>

            <rmp-autocomplete-select
              v-if="AUTO_COMPLETE_FIELD || SELECT_FIELD"
              v-model="question.value"
              :items="question.options || []"
              label="Default Value"
              index="label"
              option-key="label"
            />

            <template v-if="MULTI_CHOICE_IMAGE_FIELD && selectedQuestion.options">
              <div v-if="selectedQuestion.options.length > 0">
                <draggable
                  v-model="selectedQuestion.options"
                  class="MultiChoiceImage-container"
                  @change="handleImageReorder"
                >
                  <div
                    v-for="(option, index) in selectedQuestion.options"
                    :key="index"
                  >
                    <rmp-input
                      v-model="option.label"
                      @input="handleChangeImageLabel"
                    />
                    <span
                      class="Icon Icon-times"
                      @click="removeMultiChoiceImageOption(index)"
                    />
                  </div>
                </draggable>
                <p class="DragHint Hint">
                  <span class="Hint-icon Icon Icon-info-circle" /> Drag to reorder images
                </p>
                <rmp-checkbox
                  v-model="question.is_label_hidden"
                  :value="false"
                  label="Show Labels"
                />
              </div>
            </template>

            <rmp-checkbox
              v-if="(SELECT_FIELD || MULTI_CHOICE_IMAGE_FIELD) && selectedQuestion.supports_selection_of_multiple_options"
              v-model="question.allow_selection_of_multiple_options"
              :value="true"
              label="Allow selection of multiple options"
            />

            <rmp-input
              v-if="(AUTO_COMPLETE_FIELD || MULTI_CHOICE_IMAGE_FIELD) && selectedQuestion.supports_set_maximum_of_selectable_options"
              v-model="question.maximum_number_of_selectable_options"
              label="Maximum number of selectable options"
              input-type="number"
              :model-data="question.maximum_number_of_selectable_options || 1"
            />

            <template v-if="selectedQuestion.is_custom_question && !FILE_UPLOAD_FIELD">
              <rmp-autocomplete-select
                v-model="question.mapped_to_company_subscribed_student_custom_field_id"
                label="Map to Custom Field"
                :items="customFields"
                option-key="custom_name"
                index="id"
              />
            </template>

            <rmp-checkbox
              v-if="!textElementFieldType"
              v-model="question.is_hidden"
              :value="true"
              label="Set hidden"
            />
            <rmp-checkbox
              v-if="CHECKBOX_FIELD && customQuestion.options === null && !customYesNoCheckboxType "
              v-model="question.value"
              :value="true"
              label="Checkbox is pre-ticked?"
            />
            <div
              v-if="RATING_FIELD"
              class="StarRatings"
            >
              <label class="Form-label">Number of Stars </label>
              <rmp-radio-button
                v-for="(range, index) in selectedQuestion.options.max_ranges"
                :key="index"
                v-model="question.options.range"
                name="Rating-stars"
                :value="range"
                :label="range.toString()"
              />
              <div>
                <br>
                <rmp-input
                  v-model="question.options.min_rating_label"
                  label="Min Label"
                />
                <rmp-input
                  v-model="question.options.max_rating_label"
                  label="Max Label"
                />
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset
          v-if="event.type === 'static'"
          class="Fieldset-collapsible active"
        >
          <legend @click="toggleAccordionCollapse">
            Conditional Logic
          </legend>
          <div
            v-if="question.conditional_logic"
            class="Fieldset-body list-group collapsed"
          >
            <rmp-autocomplete-select
              v-model="question.conditional_logic.related_to_question_uuid"
              label="Show this question if"
              :items="conditionalLogicQuestionLabels"
              index="value"
              option-key="label"
              :model-data="question.conditional_logic.related_to_question_uuid"
            />
            <rmp-autocomplete-select
              v-model.trim="question.conditional_logic.constraint"
              label="Condition"
              :items="conditionalLogicConstraintOptions"
              index="value"
              option-key="label"
            />

            <div
              v-if="shouldDisplayToolTipForCheckboxQuestionsConditionalLogic(question)"
              class="u-floatRight"
            >
              <small>
                Tip, use 1 for "Yes" and 0 for "No".
              </small>
            </div>

            <rmp-input
              v-model="question.conditional_logic.value"
              label="Value(s)"
            />
          </div>
        </fieldset>

        <fieldset
          v-if="!textElementFieldType"
          class="Fieldset-collapsible active"
        >
          <legend @click="toggleAccordionCollapse">
            Messages
          </legend>
          <div class="Fieldset-body list-group collapsed">
            <p> {{ event.type === 'static' ? 'Hint' : 'Description' }}</p>
            <vue-editor
              v-if="!textElementFieldType"
              v-model="question.hint"
              class="VueEditor"
              :editor-toolbar="editorToolbar"
            />
          </div>
        </fieldset>
      </div>
    </template>
    <div class="Button-group">
      <button
        v-if="!shouldHideRemoveButton"
        class="Button Button--danger-outline"
        @click="handleRemoveQuestion(selectedQuestion)"
      >
        Remove
      </button>
      <button
        :disabled="shouldValidateFieldLabel"
        class="Button Button--green u-floatRight"
        @click="closeQuestionBuilder"
      >
        Done
      </button>
    </div>
  </div>
</template>
<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import RmpInput from '@rmpenterprise/form-input';
import RmpTextArea from '@rmpenterprise/form-text-area';
import RmpCheckbox from '@rmpenterprise/form-checkbox';
import RmpDatepicker from '@rmpenterprise/form-date-picker';
import RmpSelect from '@rmpenterprise/form-select';
import RmpAutocompleteSelect from '@rmpenterprise/form-autocomplete-select';
import RmpRadioButton from '@rmpenterprise/form-radio-button';
import { VueEditor } from 'vue2-editor';
import Draggable from 'vuedraggable';
import HeaderImageUploader from './header-image-uploader.vue';
import Tooltip from '../../common/tooltip.vue';
import FileUploadAllowedFilesDetails from '../modals/file-upload-allowed-files-details';

export default {
  components: {
    FileUploadAllowedFilesDetails,
    Draggable,
    RmpInput,
    RmpCheckbox,
    RmpTextArea,
    RmpRadioButton,
    RmpDatepicker,
    RmpSelect,
    RmpAutocompleteSelect,
    HeaderImageUploader,
    VueEditor,
    Tooltip,
  },
  data() {
    return {
      positionTop: 0,
      mountedHook: false,
      // question: null,
      editorToolbar: [
        ['bold', 'italic', 'underline'],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
      customCheckboxUuids: ['email_contact_permitted', 'phone_contact_permitted'],
      conditionalLogicConstraintOptions: [
        {
          label: 'Equals to',
          value: 'equals',
        },
        {
          label: 'Not equal to',
          value: 'notEquals',
        },
        {
          label: 'Contains',
          value: 'contains',
        },
        {
          label: 'Not contains',
          value: 'notContains',
        },
        {
          label: 'Is visible',
          value: 'displayed',
        },
      ],
      formTextElement: ['header', 'paragraph', 'statement'],
      customQuestion: {
        options: null,
      },
      ui: {
        submitLoading: false,
        showFileUploadAllowedFilesModal: false,
      },
    };
  },
  computed: {
    ...mapState('events', {
      customFields: (state) => state.lists.customFields,
      event: (state) => state.event,
    }),
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      logicWarningsShown: (state) => state.logicWarningsShown,
      questions: (state) => state.form.questions,
      formType: (state) => state.form.type,
    }),
    question: {
      get() {
        return this.selectedQuestion;
      },
      set() {
      },
    },
    TEXT_FIELD() {
      return this.selectedQuestion.type === 'text';
    },
    EMAIL_FIELD() {
      return this.selectedQuestion.type === 'email';
    },
    CUSTOM_QUESTION_FIELD() {
      return this.selectedQuestion.is_custom_question;
    },
    AUTO_COMPLETE_FIELD() {
      return this.selectedQuestion.type === 'autocomplete';
    },
    SELECT_FIELD() {
      return this.selectedQuestion.type === 'select';
    },
    CHECKBOX_FIELD() {
      return this.selectedQuestion.type === 'checkbox';
    },
    CHECKBOX_OPT_GROUP_FIELD() {
      return this.selectedQuestion.type === 'checkboxOptGroup';
    },
    DATE_FIELD() {
      return this.selectedQuestion.type === 'date';
    },
    TIME_FIELD() {
      return this.selectedQuestion.type === 'time';
    },
    PARAGRAPH_FIELD() {
      return this.selectedQuestion.type === 'paragraph';
    },
    RADIO_FIELD() {
      return this.selectedQuestion.type === 'radio';
    },
    RATING_FIELD() {
      return this.selectedQuestion.type === 'rating';
    },
    MULTI_CHOICE_IMAGE_FIELD() {
      return this.selectedQuestion.type === 'multi_choice_image';
    },
    RANKING_FIELD() {
      return this.selectedQuestion.type === 'ranking';
    },
    URL_FIELD() {
      return this.selectedQuestion.type === 'url';
    },
    STATEMENT_FIELD() {
      return this.selectedQuestion.type === 'statement';
    },
    FILE_UPLOAD_FIELD() {
      return this.selectedQuestion.type === 'file_upload';
    },
    fieldsWithDefaultValues() {
      return this.textElementFieldType || this.TEXT_FIELD || this.EMAIL_FIELD;
    },

    isUniversityQuestion() {
      return this.selectedQuestion.question_uuid === 'university'
          || this.selectedQuestion.html_form_input_id === 'university';
    },

    isDynamicForm() {
      return this.formType === 'dynamic';
    },

    customYesNoCheckboxType() {
      // Fields of question.type === 'checkbox' however the rendering is custom Yes No options
      return this.customCheckboxUuids.includes(this.selectedQuestion.question_uuid);
    },

    timeDefaultValue() {
      if (this.TIME_FIELD) {
        const splitTime = this.selectedQuestion.value ? this.selectedQuestion.value.split(':') : [];

        if (splitTime.length > 0) {
          return {
            hour: splitTime[0],
            minute: splitTime[1],
          };
        }

        return {
          hour: '',
          minute: '',
        };
      }
      return null;
    },

    shouldHideRemoveButton() {
      return this.question.key === 'form_submit_text' || this.question.key === 'header_image'
          || this.question.custom_property_to_update === 'go_to_next_question_button_label';
    },

    conditionalLogicQuestionLabels() {
      // Backend expects html_form_input_id as the value if it exists ( an event question has been saved previously )
      // If the event has not been saved then only question_uuid will exist so use this
      // Cannot add conditional logic to unsaved custom questions

      const isselectedQuestion = (q) => {
        return this.selectedQuestion.question_uuid === q.question_uuid
            && this.selectedQuestion.html_form_input_id === q.html_form_input_id;
      };

      // Unable to add conditonal logic to custom question if it has not been saved yet.
      const isAnUnsavedCustomQuestion = (q) => {
        return q.is_custom_question && q.html_form_input_id === undefined;
      };

      const questions = this.questions.filter((q) => {
        return !isselectedQuestion(q) && !isAnUnsavedCustomQuestion(q) && this.formTextElement.indexOf(q.type) === -1;
      });

      return questions
        .map((q) => {
          let label = this.stripHtmlTags(q.label);

          if (label.length > 60) {
            label = `${label.substring(0, 60)}...`;
          }
          // Having an html_form_input_id indicates the question has been saved previously
          if (q.html_form_input_id) {
            return {
              label,
              value: q.html_form_input_id,
            };
          }

          return {
            label,
            value: q.question_uuid,
          };
        });
    },
    fieldsWithCustomOptions() {
      if (this.event.checkbox_questions_that_dont_have_the_options_configuration.indexOf(this.selectedQuestion.question_uuid) !== -1) {
        return false;
      }

      return this.AUTO_COMPLETE_FIELD || this.SELECT_FIELD || this.RADIO_FIELD || this.CHECKBOX_OPT_GROUP_FIELD
          || this.CHECKBOX_FIELD || this.RANKING_FIELD;
    },
    textElementFieldType() {
      return this.formTextElement.indexOf(this.selectedQuestion.type) > -1;
    },
    labelIsRequiredValidationError() {
      return this.shouldValidateFieldLabel ? { errors: [{ message: 'Label is required' }] } : {};
    },
    shouldValidateFieldLabel() {
      return this.question.label === '';
    },
    submitButtonText: {
      get() {
        return this.event.form_submit_text;
      },
      set(val) {
        this.updateEventContent({ form_submit_text: val });
      },
    },
  },

  mounted() {
    if (this.formType === 'dynamic') {
      this.setDynamicFormConfig();
    } else {
      this.calculatePanelScrollPosition();
      this.increaseBodyScrollHeight();
      window.addEventListener('scroll', this.calculatePanelScrollPosition);
    }

    // mounted is used for question-builder-close directive. Stops execution immediately as the component is mounted
    this.mountedHook = true;
    this.setInitialQuestionState();
    if (!this.RATING_FIELD && this.selectedQuestion.options) {
      this.populateOptionsList();
    }
  },

  methods: {
    ...mapMutations('events', ['updateEventContent']),
    ...mapActions('events', ['updateEvent']),
    ...mapMutations('forms', ['updateSelectedQuestion', 'resetSelectedQuestion', 'updateLogicWarnings']),
    ...mapActions('forms', ['removeFormQuestion']),

    /**
     * When the user wants to setup an equals or not equals conditional logic for a checkbox type question, the
     * ui displays a tip. This means reminds the user that they can use 1 for Yes and 0 for No.
     * @param selectedQuestion
     * @returns {boolean}
     */
    shouldDisplayToolTipForCheckboxQuestionsConditionalLogic(selectedQuestion) {
      const appliedForConditions = ['equals', 'notEquals'];
      const questionUUIDThatConditionalLogicLooksTo = selectedQuestion.conditional_logic.related_to_question_uuid;
      const questionThatConditionalLogicLooksTo = this.questions.filter((question) => question.html_form_input_id === questionUUIDThatConditionalLogicLooksTo);

      if (!questionThatConditionalLogicLooksTo.length) {
        return false;
      }

      return questionThatConditionalLogicLooksTo[0].type === 'checkbox' && appliedForConditions.indexOf(selectedQuestion.conditional_logic.constraint) > -1;
    },

    populateOptionsList() {
      this.customQuestion.options = this.selectedQuestion.options
        .map((option) => `${option.label}\n`)
        .join('')
        .trim();
    },
    setInitialQuestionState() {
      if (!this.question.conditional_logic) {
        this.question.conditional_logic = { related_to_question_uuid: null };
      }
    },

    setDynamicFormConfig() {
      this.positionTop = '80';
    },

    showQuestionEditsLogicWarning() {
      this.$bus.$emit('displayMsg', {
        text: 'Adding or removing question options can change screen flows and scores already set up for this form. Please check them again in the Logic tab',
        type: 'warning',
        duration: 8000,
      });
    },

    handleChangeImageLabel() {
      if (!this.logicWarningsShown.addQuestionOptions && (this.question.has_flows || this.question.has_scores)) {
        this.showQuestionEditsLogicWarning();
        this.updateLogicWarnings('addQuestionOptions');
      }
    },

    handleImageReorder() {
      if (!this.logicWarningsShown.reorderQuestionOptions && (this.question.has_flows || this.question.has_scores)) {
        this.$bus.$emit('displayMsg', {
          text: 'Reordering question options can change screen flows and scores already set up for this form. Please check them again in the Logic tab',
          type: 'warning',
          duration: 8000,
        });
        this.updateLogicWarnings('reorderQuestionOptions');
      }
    },

    removeMultiChoiceImageOption(optionIndex) {
      const options = [...this.selectedQuestion.options];

      options.splice(optionIndex, 1);

      this.question = {
        ...this.selectedQuestion,
      };
      this.question.options = options;

      if (!this.logicWarningsShown.addQuestionOptions && (this.question.has_flows || this.question.has_scores)) {
        this.showQuestionEditsLogicWarning();
        this.updateLogicWarnings('addQuestionOptions');
      }
    },

    handleAddToOptionsList() {
      if (!this.logicWarningsShown.addQuestionOptions && (this.question.has_flows || this.question.has_scores)) {
        this.showQuestionEditsLogicWarning();
        this.updateLogicWarnings('addQuestionOptions');
      }
      if (this.customQuestion.options.length === 0) {
        this.question.options = null;

        // Backend expects checkboxOptGroup with no options to be of type checkbox (just a single label value)
        if (this.question.type === 'checkboxOptGroup') {
          this.question.type = 'checkbox';
        }

        return true;
      }

      // Check for new line inside options list - New line is the separator for each options
      if (Array.isArray(this.customQuestion.options.match(/\n/g))
          || this.customQuestion.options.length !== 0) {
        this.question.options = this.customQuestion.options.split(/\n/g)
          .map((option) => ({
            label: option,
            value: option,
          }))
          .filter((opt) => opt.label.trim() !== '');

        // Backend expects checkbox questions with multiple options to be of type checkboxOptGroup
        if (this.question.type === 'checkbox') {
          this.question.type = 'checkboxOptGroup';
        }
      }

      return true;
    },
    closeQuestionBuilder() {
      if (this.mountedHook) {
        this.mountedHook = false;
      } else if (!this.shouldValidateFieldLabel) {
        this.increaseBodyScrollHeight(false);
        this.updateSelectedQuestion(this.question);
        this.resetSelectedQuestion();
      }
    },
    handleRemoveQuestion(question) {
      this.increaseBodyScrollHeight(false);

      this.removeFormQuestion(question);
    },

    stripHtmlTags(string) {
      return string.replace(/(<([^>]+)>)/ig, ' ');
    },

    updateTimeDefaultValue() {
      this.$set(this.question, 'value', `${this.timeDefaultValue.hour}:${this.timeDefaultValue.minute}`);
    },

    calculatePanelScrollPosition() {
      // Calculate scroll position based on longer forms and scrolling the browser window
      // Question panel tracks question and does not scroll with question configuration pane
      this.$nextTick(() => {
        const selectedQuestionContainer = document.querySelector('.FormQuestionsContainer .Selected.Question-container');
        if (selectedQuestionContainer === null) return false;
        this.positionTop = selectedQuestionContainer.getBoundingClientRect().top - 100;
        return true;
      });
    },

    increaseBodyScrollHeight(increase = true) {
      // Increase scroll height to give users enough space to edit questions at the bottoms of the form with a lot of options
      if (increase) {
        document.body.style.height = `${document.querySelector('.FormBuilder').clientHeight + 1000}px`;
      } else {
        document.body.style.height = `${document.querySelector('.FormBuilder').clientHeight - 1000}px`;
      }
    },

    toggleAccordionCollapse(ev) {
      const fieldset = ev.target.parentElement;
      const body = ev.target.parentElement.querySelector('.Fieldset-body');

      fieldset.classList.toggle('active');
      body.classList.toggle('collapsed');
    },
    getNumberArray(start, end, increment = 1) {
      // Helper function can probably be added to a utlitiies file or the store.
      // Taken from SMS code base to genenerate years
      const len = Math.floor((end - start) / increment) + 1;
      // Get numbers and add 0 to any number that's less than 2
      return Array(len)
        .fill()
        .map((_, index) => String(start + (index * increment))
          .padStart(2, 0));
    },
  },

};
</script>
<style scoped lang="scss">

::v-deep .v-select.single .selected-tag {
  line-height: 1.2;
}

.QuestionConfigurationBuilder {
  position: fixed;
  width: 100%;
  max-width: 439px;
  padding: 25px;
  top: 0;
  z-index: 2;
  border: 1px solid #E3E3E3;
  border-radius: 6px;
  background-color: #FFFFFF;
  box-shadow: 0 13px 29px 0 rgba(0, 0, 0, 0.11);

  .UniversityQuestion{
    margin-bottom: 10px;
  }

  &.dynamic {
    position: absolute;
  }

  .Selected {
    &.QuestionUuid {
      background: #046FF0;
      color: white;
      padding: 4px 15px;
      text-transform: capitalize;
      margin-bottom: -2px;
      font-size: .8rem;
    }

    &.Question-container {
      border: 1px solid #046FF0;
    }
  }

  .MultiChoiceImage-container {
    background: #EEF1F3;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #DDDDDD;
    align-self: baseline;

    .Form-field {
      margin: 6px 3px 0 0;
      width: 94%;
      display: inline-block;
    }

    ::v-deep .Form-input {
      height: 28px;
    }
  }

  .DragHint {
    margin-bottom: 10px;
  }

  .accordion {
    max-height: 400px;
    overflow: auto;
    overflow-x: hidden;
  }

  legend {
    padding: 15px 0;
    background: none;
    font-weight: bold;

    &::after {
      color: black !important;
    }
  }

  .Fieldset-body {
    padding: 6px 10px;
  }

  .QuestionUuid-header {
    text-transform: capitalize;
  }

  .Icon.arrow-left {
    display: block;
    position: absolute;
    left: -12px;
    top: 108px;
    width: 0;
    height: 0;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    border-right: 12px solid #ffffff;
    z-index: 1;
  }
}

.QuestionDisplayedAs {
  margin-bottom: 10px;
}

.QuestionDisplayedAs-Info {
  margin-bottom: 20px;
}

.Fieldset-collapsible {
  .Fieldset-body.collapsed {
    display: none;
  }
}

.FileUploadAllowedFilesHelp {
  cursor: pointer;
  color: blue;
}

.VueEditor {
  margin-bottom: 8px;

  ::v-deep .ql-snow .ql-tooltip {
    left: 0 !important;
    margin-top: 43px;
  }

  ::v-deep .ql-editor {
    max-height: 100px;
    min-height: 100px;
  }
}

// Reset the line-height for question build rich text editor
::v-deep .ql-editor {
  blockquote, h1, h2, h3, h4, h5, h6, ol, p, pre, ul {
    line-height: inherit;
  }
}

.FileUploadAllowedFileTypeCheckboxes {

  .AllowedFileTypeExamples {
    margin-bottom: 15px;
  }

  ::v-deep .Form-field--checkbox {
    margin-bottom: 10px;
  }
}
</style>
